import React, { ChangeEvent } from 'react'
import { BankrateLoanTerms } from '@bankrate/mortgage-types'

import FHA from 'components/filters/inputs/FHA'
import { useStoreActions, useStoreState } from 'store'
import MilitaryStatus from 'components/filters/inputs/MilitaryStatus'

type Term = { label: string; value: BankrateLoanTerms[] }
const termList: Term[] = [
  {
    label: '30 yr fixed',
    value: ['30yr'],
  },
  {
    label: '20 yr fixed',
    value: ['20yr'],
  },
  {
    label: '15 yr fixed',
    value: ['15yr'],
  },
  {
    label: '10 yr fixed',
    value: ['10yr'],
  },
  {
    label: '3 yr ARM',
    value: ['3-1arm', '3-6arm'],
  },
  {
    label: '5 yr ARM',
    value: ['5-1arm', '5-6arm'],
  },
  {
    label: '7 yr ARM',
    value: ['7-1arm', '7-6arm'],
  },
  {
    label: '10 yr ARM',
    value: ['10-1arm', '10-6arm'],
  },
]

export default function LoanOptions() {
  const { onInputTracking } = useStoreActions(actions => actions.tracking)
  const mortgageType = useStoreState(state => state.dimensions.dimensions.mortgageType)
  const updateLoanTerms = useStoreActions(actions => actions.dimensions.updateLoanTerms)
  const purchaseLoanTerms = useStoreState(state => state.dimensions.dimensions.purchase.loanTerms)
  const refinanceLoanTerms = useStoreState(state => state.dimensions.dimensions.refinance.loanTerms)
  const isDesktop = useStoreState(state => state.dimensions.isDesktop)

  const handleChange = (term: Term) => (event: ChangeEvent<HTMLInputElement>) => {
    const selectedTerms = mortgageType === 'Purchase' ? purchaseLoanTerms : refinanceLoanTerms

    // Check what the new state will be and prevent changing
    // it if we will not have any terms, since multiple terms
    // can be added from a single ARM selection
    let newTerms = [...selectedTerms]
    term.value.forEach(singleTerm => {
      const index = newTerms.indexOf(singleTerm)

      if (index >= 0) {
        newTerms.splice(index, 1)
      }
    })

    if (newTerms.length === 0) {
      event.preventDefault()
      event.target.checked = true
      return
    }

    updateLoanTerms(term.value)
    // Since term value is an array with multiple loanOptions in arm products,
    // the data team suggested we also use the label in the name field.
    onInputTracking({
      name: term.value.length > 1 ? term.label : term.value[0],
      type: 'TERM',
      label: term.label,
      value: event.target.checked,
    })
  }

  const switchStyle = `display-flex align-middle py-4 ${isDesktop ? 'border-t border-gray' : ''}`

  return (
    <div className="loan-options__container md:p-4 mt-8 md:mt-0">
      <p className="type-body-two mb-4 font-circular-bold" style={{ width: '80%' }}>
        Loan terms
      </p>
      <div className="display-flex flex-wrap py-4" id="mortgageTerms">
        {termList.map((term, index) => {
          return (
            <div key={`${term.value}-${index}`} className="w-1/2 md:w-1/3 lg:w-1/2 mb-4 px-1">
              <span className="FormPill FormPill--small w-full">
                <input
                  value={term.value}
                  className="FormPill-input"
                  type="checkbox"
                  id={`checkbox-${term.value}`}
                  defaultChecked={
                    mortgageType === 'Purchase'
                      ? purchaseLoanTerms.includes(term.value[0])
                      : refinanceLoanTerms.includes(term.value[0])
                  }
                  onChange={handleChange(term)}
                />
                <label htmlFor={`checkbox-${term.value}`} className="FormPill-label w-full text-center">
                  {term.label}
                </label>
              </span>
            </div>
          )
        })}
      </div>

      <div className={switchStyle}>
        <div className="w-2/3">
          <p className="type-body-two mb-1 font-circular-bold">Show FHA loans</p>
          <p className="type-body-three text-slate mb-0">
            Available for borrowers with {'<10%'} down payment or low credit score
          </p>
        </div>
        <div className="w-1/3">
          <FHA />
        </div>
      </div>

      <div className={switchStyle}>
        <div className="w-2/3">
          <p className="type-body-two mb-1 font-circular-bold">Show VA loans</p>
          <p className="type-body-three text-slate mb-0"> Self or spouse must be active or retired military</p>
        </div>
        <div className="w-1/3">
          <MilitaryStatus />
        </div>
      </div>
    </div>
  )
}
